import type { AppState, RevisionEntry } from '../types'
import { BACKUP_KEY, REVISION_KEY, STORE_KEY } from '../domain/constants'
import { initialState } from '../domain/seed'
import { normalizeState } from '../domain/validation'

export function readJson<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    if (!value) return fallback
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

export function writeJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value))
}

export function loadState(): AppState {
  return normalizeState(readJson<unknown>(STORE_KEY, initialState()))
}

export function saveState(state: AppState): void {
  writeJson(STORE_KEY, normalizeState(state))
}

export function createAutomaticBackup(state: AppState, reason: string): void {
  const backups = readJson<Array<{ id: string; reason: string; timestamp: string; state: AppState }>>(BACKUP_KEY, [])
  const last = backups.at(-1)
  const now = Date.now()
  if (last && now - Date.parse(last.timestamp) < 1000 * 60 * 3 && reason === 'autosave') return
  backups.push({
    id: crypto.randomUUID?.() ?? `${now}`,
    reason,
    timestamp: new Date(now).toISOString(),
    state: normalizeState(state)
  })
  writeJson(BACKUP_KEY, backups.slice(-12))
}

export function listAutomaticBackups() {
  return readJson<Array<{ id: string; reason: string; timestamp: string; state: AppState }>>(BACKUP_KEY, [])
}

export function recordRevision(entry: Omit<RevisionEntry, 'id' | 'timestamp'>): void {
  const revisions = readJson<RevisionEntry[]>(REVISION_KEY, [])
  revisions.push({
    ...entry,
    id: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    timestamp: new Date().toISOString()
  })
  writeJson(REVISION_KEY, revisions.slice(-500))
}

export function listRevisions(): RevisionEntry[] {
  return readJson<RevisionEntry[]>(REVISION_KEY, [])
}
